import { Square, Sparkles } from 'lucide-react';
import { useTimeTracking } from '@/context/TimeTrackingContext';
import { formatDuration } from '@/services/timeTrackingService';

interface RunningTimerWidgetProps {
  /** Pixels to offset from the left so the widget clears the sidebar. */
  leftOffset: number;
}

/**
 * Tiny persistent "focus session" widget shown bottom-left (clearing the
 * sidebar) whenever a task timer is running. Visible on every page.
 */
export default function RunningTimerWidget({ leftOffset }: RunningTimerWidgetProps) {
  const { activeEntry, activeElapsed, stopActive } = useTimeTracking();

  if (!activeEntry) return null;

  return (
    <div
      className="fixed z-[9998] select-none"
      style={{ left: leftOffset, bottom: 88 }}
    >
      <div className="flex items-center gap-3 rounded-full bg-gray-900 text-white pl-3 pr-2 py-2 shadow-2xl border border-white/10">
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>

        <div className="min-w-0 max-w-[180px]">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-300">
            <Sparkles className="h-3 w-3" />
            Focusing
          </div>
          <div className="truncate text-sm font-medium leading-tight" title={activeEntry.taskTitle}>
            {activeEntry.taskTitle}
          </div>
        </div>

        <div className="font-mono text-sm tabular-nums text-white/90 min-w-[52px] text-right">
          {formatDuration(activeElapsed)}
        </div>

        <button
          onClick={() => stopActive()}
          className="flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-xs font-medium transition-colors"
          title="Finish this focus session"
        >
          <Square className="h-3.5 w-3.5 fill-current" />
          Done
        </button>
      </div>
    </div>
  );
}
